import * as React from "react";
import {
  Link,
  Switch,
  Route,
  useRouteMatch,
  useHistory,
  useParams,
} from "react-router-dom";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { toast } from "react-toastify";

// Components
import InputText from "../InputText/InputText";
import InputSelectRegister from "../InputSelect/InputSelectRegister";
import Button from "../Button/Button";
import Loader from "../Loader/Loader";
import ChangeLanguage from "../ChangeLanguage/ChangeLanguage";

// Utils
import useLanguageString from "../../hooks/LanguageStrings.hooks";
import {
  REGISTER,
  REGISTER_USER_PERMISSIONS,
} from "../../queries/User.queries";

// Styles
import "./Register.scss";
import RegisterIllustration from "../../static/images/login-illustration.svg";
import CompletedIllustration from "../../static/images/order-completed-illustration.svg";
import SonnenLogo from "../../static/images/logo-sonnen-white.svg";

const QUERY_PERMISSIONS_LIST = gql`
  ${REGISTER_USER_PERMISSIONS}
`;

const MUTATION_REGISTER = gql`
  ${REGISTER}
`;

const Register: React.FC = () => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const params: any = useParams();
  const [strings] = useLanguageString(params.countryCode, "register");
  const [permissionOptions, setPermissionOptions] = React.useState<any[]>([]);
  const [register, { loading: registerPending }] =
    useMutation(MUTATION_REGISTER);
  const { loading, data: permissionData } = useQuery(QUERY_PERMISSIONS_LIST, {
    onCompleted: (data) => {
      const { userPermissionsList } = data;

      setPermissionOptions(
        userPermissionsList.filter(
          (userPermission: any) => userPermission.active
        )
      );
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      company: "",
      permissions: permissionData ? permissionData[0] : "",
    },
    onSubmit: (values) => {
      register({
        variables: {
          ...values,
          email: values.username,
          firstName: values.name,
          permissions: values.permissions.value,
          countryCode: params.countryCode ? params.countryCode : "en",
        },
      })
        .then((response) => {
          if (response.data.registerUser) {
            history.push(`/${params.countryCode}/register/success`);
          }
        })
        .catch((error) => {
          if (
            error.message.indexOf(
              "Diese E-Mail-Adresse wurde bereits registriert"
            )
          ) {
            toast.error(strings?.register.stringError);
          }
        });
    },
  });

  return (
    <div className="register">
      <div className="register-container">
        <div className="register-container__inner">
          <div className="register-container__header">
            <img src={SonnenLogo} alt="sonnen" />
          </div>
          {!strings && <Loader />}
          {strings && (
            <Switch>
              <Route exact path={path}>
                <div className="register-container__intro">
                  <div className="register-container__intro__illustration">
                    <img src={RegisterIllustration} alt="Neue Registrierung" />
                  </div>
                  <h1>{strings.register.stringHeadline}</h1>
                </div>
                <form onSubmit={formik.handleSubmit}>
                  <InputText
                    id="name"
                    label={strings.register.stringName}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    required
                  />
                  <InputText
                    id="username"
                    label={strings.register.stringMail}
                    type="email"
                    className="mt-3"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    required
                  />
                  <InputText
                    id="company"
                    label={strings.register.stringCompany}
                    className="mt-3"
                    value={formik.values.company}
                    onChange={formik.handleChange}
                    required
                  />
                  <InputSelectRegister
                    id="permissions"
                    label={`${strings.register.stringLocation}*`}
                    placeholder={strings.register.stringLocationPlaceholder}
                    className="mt-3"
                    options={permissionOptions}
                    value={formik.values.permissions}
                    onChange={formik.setFieldValue}
                    onBlur={formik.setFieldTouched}
                    loading={loading}
                    required={true}
                  />
                  <Button loading={registerPending} className="mt-4">
                    {strings.register.stringButton}
                  </Button>
                </form>
                <p className="register-container__register text-muted">
                  {strings.register.stringAccountRegistered}{" "}
                  <Link to={`/${params.countryCode}/login`}>
                    {strings.register.stringAccountRegisteredLink}
                  </Link>
                </p>
                <ChangeLanguage />
              </Route>
              <Route path={`${path}/success`}>
                <div className="register-container__intro">
                  <img src={CompletedIllustration} alt="Briefumschlag" />
                  <h1>{strings.register.stringSuccessHeadline}</h1>
                  <p>{strings.register.stringSuccessBody}</p>
                  <Link
                    className="button my-4"
                    to={`/${params.countryCode}/login`}
                  >
                    {strings.register.stringSuccessBack}
                  </Link>
                </div>
              </Route>
            </Switch>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
